// src/utils/imageOptimizer.js
/**
 * Utility functions for validating and optimizing homepage slider images.
 * Adheres to:
 * - Allowed formats: JPG/JPEG, PNG, WebP
 * - Max original size: 5 MB
 * - Resizing: max width 1920px preserving exact aspect ratio
 * - Compression: WebP output (quality ~0.88) where browser support is available,
 *   preserving high banner quality without unnecessary file bloat.
 */

export const ALLOWED_IMAGE_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp'
];

export const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp'];
export const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB
export const MAX_BANNER_WIDTH = 1920;

/**
 * Validate a user-selected slider image file.
 * @param {File} file
 * @throws {Error} with friendly message if invalid
 */
export const validateSliderFile = (file) => {
  if (!file) {
    throw new Error('Please select an image file to upload.');
  }

  // Check MIME type
  const mimeType = file.type?.toLowerCase();
  const fileExt = file.name ? file.name.split('.').pop()?.toLowerCase() : '';

  const isTypeValid = ALLOWED_IMAGE_TYPES.includes(mimeType) || ALLOWED_EXTENSIONS.includes(fileExt);
  if (!isTypeValid) {
    throw new Error('Unsupported image format. Please upload a JPG, PNG, or WebP image.');
  }

  // Check file size
  if (file.size > MAX_FILE_SIZE_BYTES) {
    const sizeInMb = (file.size / (1024 * 1024)).toFixed(2);
    throw new Error(`The selected image is ${sizeInMb} MB. Maximum allowed size is 5 MB.`);
  }

  return true;
};

/**
 * Optimizes an image using HTML5 Canvas:
 * - Downscales if width > 1920px while preserving the exact aspect ratio.
 * - Converts to WebP format if supported by browser.
 * - Leaves quality high (~0.88) to prevent banner degradation.
 * 
 * @param {File} file - The original image file
 * @param {Object} options - Optional configuration overrides
 * @returns {Promise<{ blob: Blob, contentType: string, extension: string, width: number, height: number, originalSize: number, optimizedSize: number }>}
 */
export const optimizeSliderImage = (file, options = {}) => {
  return new Promise((resolve, reject) => {
    try {
      validateSliderFile(file);
    } catch (err) {
      return reject(err);
    }

    const maxWidth = options.maxWidth || MAX_BANNER_WIDTH;
    const quality = options.quality !== undefined ? options.quality : 0.88;

    const objectUrl = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);

      const naturalWidth = img.naturalWidth || img.width;
      const naturalHeight = img.naturalHeight || img.height;

      if (!naturalWidth || !naturalHeight) {
        return reject(new Error('Unable to read image dimensions.'));
      }

      // Calculate aspect-ratio-preserving dimensions
      let targetWidth = naturalWidth;
      let targetHeight = naturalHeight;

      if (naturalWidth > maxWidth) {
        targetWidth = maxWidth;
        targetHeight = Math.round((naturalHeight * maxWidth) / naturalWidth);
      }

      // Render to offscreen canvas
      const canvas = document.createElement('canvas');
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        return reject(new Error('Could not initialize canvas context for optimization.'));
      }

      // Use high quality image smoothing
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

      // Attempt to export as WebP
      canvas.toBlob(
        (webpBlob) => {
          if (webpBlob && webpBlob.type === 'image/webp') {
            resolve({
              blob: webpBlob,
              contentType: 'image/webp',
              extension: 'webp',
              width: targetWidth,
              height: targetHeight,
              originalSize: file.size,
              optimizedSize: webpBlob.size,
            });
          } else {
            // WebP not supported by this browser's canvas; fallback to original format or JPEG
            const fallbackType = ALLOWED_IMAGE_TYPES.includes(file.type) ? file.type : 'image/jpeg';
            const fallbackExt = fallbackType === 'image/png' ? 'png' : 'jpg';

            canvas.toBlob(
              (fallbackBlob) => {
                const finalBlob = fallbackBlob || file;
                resolve({
                  blob: finalBlob,
                  contentType: fallbackType,
                  extension: fallbackExt,
                  width: targetWidth,
                  height: targetHeight,
                  originalSize: file.size,
                  optimizedSize: finalBlob.size,
                });
              },
              fallbackType,
              quality
            );
          }
        },
        'image/webp',
        quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to load image for processing. The file may be corrupt.'));
    };

    img.src = objectUrl;
  });
};

/**
 * Format bytes to readable string (e.g. 1.2 MB, 450 KB).
 */
export const formatBytes = (bytes, decimals = 1) => {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};
